import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiInfo,
  FiFileText,
  FiCalendar,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiMapPin,
  FiActivity,
  FiUser,
  FiPackage,
  FiTrendingUp
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import axiosInstance from '../../libs/axios';
import EntityLogs from '../../utils/logs.utils';

interface Billing {
  id: number;
  project_site_id: number;
  tc_po_number: string;
  tc_po_date: string;
  is_po_given_at_allocation: boolean;
  billing_status: string;
  activity_amount: string;
  billed_site_id: string;
  billed_ref_id: string;
  billed_po_number: string;
  billed_po_date: string;
  wcc_date: string;
  wcc_number: string;
  wcc_status: string;
  receipt_number: string;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: string;
  invoice_status: string;
  manual_site_name: string | null;
  manual_site_reference: string | null;
  manual_client_allocation_date: string | null;
  manual_circle_name: string | null;
  manual_project_name: string | null;
  manual_activity_name: string | null;
  created_at: string;
  updated_at: string;
  project_site: {
    id: number;
    site_id: string;
    site_name: string;
    project: {
      id: number;
      project_name: string;
      project_short_name: string;
      activity: {
        id: number;
        activity_name: string;
      };
    };
    state_circle: {
      id: number;
      state_name: string;
      circle_name: string;
    };
  };
}

interface TabProps {
  billing: Billing;
}

const BillingOverviewTab: React.FC<TabProps> = ({ billing }) => {
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'wcc_approved': return 'text-green-600 bg-green-100';
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-4">
      {/* Basic Billing Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Billing Information</h3>
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(billing.billing_status)}`}>
            {billing.billing_status.replace('_', ' ').toUpperCase()}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            {/* <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Billing ID</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">#{billing.id}</span>
              </div>
            </div> */}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Billed Site ID</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiMapPin className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{billing.billed_site_id}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Billed Reference ID</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{billing.billed_ref_id}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Activity Amount</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FaRupeeSign className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{parseFloat(billing.activity_amount).toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">PO Given at Allocation</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                {billing.is_po_given_at_allocation ? (
                  <FiCheckCircle className="text-green-500" size={14} />
                ) : (
                  <FiAlertCircle className="text-red-500" size={14} />
                )}
                <span className="text-sm text-gray-900">
                  {billing.is_po_given_at_allocation ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Receipt Number</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{billing.receipt_number}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Created Date</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{formatDate(billing.created_at)}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last Updated</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{formatDate(billing.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Order Details */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Purchase Order Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">TC PO Number</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{billing.tc_po_number}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">TC PO Date</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{formatDate(billing.tc_po_date)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Billed PO Number</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{billing.billed_po_number}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Billed PO Date</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{formatDate(billing.billed_po_date)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WCC Details */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">WCC Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">WCC Number</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <FiFileText className="text-gray-400" size={14} />
              <span className="text-sm text-gray-900">{billing.wcc_number}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">WCC Date</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <FiCalendar className="text-gray-400" size={14} />
              <span className="text-sm text-gray-900">{formatDate(billing.wcc_date)}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">WCC Status</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(billing.wcc_status)}`}>
                {billing.wcc_status.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Invoice Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Number</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{billing.invoice_number}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Date</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{formatDate(billing.invoice_date)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Amount</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FaRupeeSign className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900 font-semibold">
                  {parseFloat(billing.invoice_amount).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Invoice Status</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(billing.invoice_status)}`}>
                  {billing.invoice_status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectSiteTab: React.FC<TabProps> = ({ billing }) => {
  const { project_site } = billing;

  return (
    <div className="space-y-4">
      {/* Site Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Site Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Site ID</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <FiMapPin className="text-gray-400" size={14} />
              <span className="text-sm text-gray-900">{project_site.site_id}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Site Name</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <FiMapPin className="text-gray-400" size={14} />
              <span className="text-sm text-gray-900">{project_site.site_name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Project Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Project Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Project Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiPackage className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{project_site.project.project_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Project Short Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiPackage className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{project_site.project.project_short_name}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Activity</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiActivity className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{project_site.project.activity.activity_name}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* State Circle Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Location Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <FiMapPin className="text-gray-400" size={14} />
              <span className="text-sm text-gray-900">{project_site.state_circle.state_name}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Circle</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <FiMapPin className="text-gray-400" size={14} />
              <span className="text-sm text-gray-900">{project_site.state_circle.circle_name}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ViewBilling() {
  const { id } = useParams<{ id: string }>();
  const [billing, setBilling] = useState<Billing | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiInfo },
    { id: 'project', label: 'Project & Site', icon: FiMapPin },
    { id: 'logs', label: 'Logs', icon: FiClock }
  ];

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const response = await axiosInstance.get(`/billing/${id}`);
        setBilling(response.data.data.billing);
      } catch (error) {
        console.error('Error fetching billing:', error);
      }
      setLoading(false);
    };

    fetchBilling();
  }, [id]);

  const renderTabContent = () => {
    if (!billing) return null;

    switch (activeTab) {
      case 'overview':
        return <BillingOverviewTab billing={billing} />;
      case 'project':
        return <ProjectSiteTab billing={billing} />;
      case 'logs':
        return <EntityLogs entity="billing" entityId={id as string} />;
      default:
        return <BillingOverviewTab billing={billing} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading billing details...</p>
        </div>
      </div>
    );
  }

  if (!billing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Billing Not Found</h2>
          <p className="text-gray-600">The billing record you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Billing Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded flex items-center justify-center text-white text-sm font-bold">
              B{billing.id}
            </div>

            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">
                Billing #{billing.id}
              </h1>
              <p className="text-sm text-gray-600 mb-1">{billing.billed_ref_id}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <FiMapPin size={12} />
                  {billing.project_site.site_name}
                </div>
                <div className="flex items-center gap-1">
                  <FaRupeeSign size={12} />
                  Invoice: {parseFloat(billing.invoice_amount).toLocaleString('en-IN')}
                </div>
                <div className="flex items-center gap-1">
                  <FiCalendar size={12} />
                  {new Date(billing.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
}